import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { ChevronsUpDown, Check, UserRound, Download, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOLLOW = '__follow__';

/**
 * Individual setups (Settings > User Interface > User Spaces, beside the Spaces themselves). Everyone follows the Space of their Business Unit Group unless they are set
 * up here: pick a person from this branch, then give them their own default Space and/or let them switch to additional Spaces (none to begin with). Changes save at once.
 * "Move to another deployment" exports / imports the whole set (Spaces with their dashboards, the group mapping and these setups).
 */
export default function UserSpacesPanel({ branchId, sectors, onImported }) {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!branchId) return;
    api.get(`${API}/nav-header/user-spaces`, { params: { branch_id: branchId } })
      .then((res) => setUsers(res.data?.users || []))
      .catch(() => { setUsers([]); toast.error('Could not load the people for this branch'); });
  }, [branchId]);
  useEffect(() => { setSelectedId(null); setUsers([]); load(); }, [load]);

  const nameOf = useCallback((id) => (sectors.find((s) => s.id === id) || {}).name || '', [sectors]);
  const selected = users.find((u) => u.id === selectedId) || null;
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? users.filter((u) => `${u.full_name} ${u.email}`.toLowerCase().includes(q)) : users;
  }, [users, search]);

  const save = async (patch) => {
    if (!selected) return;
    const next = { default_sector_id: selected.default_sector_id, additional_sector_ids: selected.additional_sector_ids, ...patch };
    setSaving(true);
    try {
      const res = await api.put(`${API}/nav-header/user-spaces/${selected.id}`, { branch_id: branchId, default_sector_id: next.default_sector_id, additional_sector_ids: next.additional_sector_ids });
      setUsers((list) => list.map((u) => (u.id === selected.id ? { ...u, default_sector_id: res.data.default_sector_id, additional_sector_ids: res.data.additional_sector_ids } : u)));
      toast.success(`${selected.full_name}'s Spaces saved`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const effectiveDefault = selected ? (selected.default_sector_id || selected.profile_sector_id) : null;
  const extraChoices = sectors.filter((s) => s.id !== effectiveDefault);
  const toggleExtra = (id) => {
    const has = selected.additional_sector_ids.includes(id);
    save({ additional_sector_ids: has ? selected.additional_sector_ids.filter((x) => x !== id) : [...selected.additional_sector_ids, id] });
  };

  const exportAll = async () => {
    try {
      const res = await api.get(`${API}/nav-header/spaces-export`, { params: { branch_id: branchId } });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `User-Spaces-${branchId}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Spaces file saved');
    } catch (error) {
      toast.error('Failed to export the Spaces');
    }
  };
  const importFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    let data;
    try { data = JSON.parse(await file.text()); } catch (_) { toast.error('That file could not be read'); return; }
    try {
      const res = await api.post(`${API}/nav-header/spaces-import`, { branch_id: branchId, data });
      const r = res.data || {};
      toast.success(`Imported: ${r.spaces_created} new and ${r.spaces_updated} updated Spaces, ${r.groups_set} groups, ${r.people_set} people`);
      load();
      if (onImported) onImported();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to import the Spaces');
    }
  };

  return (
    <Card data-testid="user-spaces-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><UserRound className="w-5 h-5 text-orange-500" /> Individual setups</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Person (this branch)</Label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button type="button" className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm" data-testid="user-spaces-person-picker">
                <span className="truncate">{selected ? selected.full_name : 'Choose a person…'}</span>
                <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-2 bg-white" align="start">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…" className="h-8 mb-2" data-testid="user-spaces-person-search" />
              <div className="max-h-64 overflow-y-auto">
                {shown.length === 0 && <p className="text-sm text-gray-400 px-2 py-3">No one found.</p>}
                {shown.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { setSelectedId(u.id); setPickerOpen(false); setSearch(''); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm hover:bg-orange-50"
                    data-testid={`user-spaces-person-${u.id}`}
                  >
                    <Check className={`h-4 w-4 shrink-0 ${u.id === selectedId ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{u.full_name}</span>
                      <span className="block truncate text-[11px] text-gray-500">{nameOf(u.default_sector_id || u.profile_sector_id) || 'No Space'}{u.default_sector_id || u.additional_sector_ids.length ? ' · set up individually' : ''}</span>
                    </span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {selected && (
          <div className="space-y-4 rounded-xl border p-3" data-testid="user-spaces-person-setup">
            <div>
              <p className="text-sm font-semibold">{selected.full_name}</p>
              <p className="text-xs text-gray-500">{selected.email}{selected.role ? ` · ${selected.role.replace('_', ' ')}` : ''}</p>
            </div>
            <div>
              <Label>Default Space</Label>
              <Select value={selected.default_sector_id || FOLLOW} onValueChange={(v) => save({ default_sector_id: v === FOLLOW ? null : v })} disabled={saving}>
                <SelectTrigger data-testid="user-spaces-default-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value={FOLLOW}>Follow their setup ({nameOf(selected.profile_sector_id) || 'Default'})</SelectItem>
                  {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">The side bar and the dashboard they start with.</p>
            </div>
            <div>
              <Label>Additional Spaces</Label>
              <Popover open={extraOpen} onOpenChange={setExtraOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm" data-testid="user-spaces-additional-trigger">
                    <span className="truncate">{selected.additional_sector_ids.length ? selected.additional_sector_ids.map(nameOf).filter(Boolean).join(', ') : 'None'}</span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-1 bg-white" align="start">
                  {extraChoices.length === 0 && <p className="text-sm text-gray-400 px-2 py-3">There are no other Spaces yet.</p>}
                  {extraChoices.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-orange-50 cursor-pointer">
                      <input type="checkbox" checked={selected.additional_sector_ids.includes(s.id)} onChange={() => toggleExtra(s.id)} disabled={saving} data-testid={`user-spaces-additional-${s.id}`} />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-gray-500 mt-1">Spaces they can switch to from the side bar, on top of their default. None unless you tick them.</p>
            </div>
          </div>
        )}

        <div className="border-t pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Move to another deployment</p>
          <p className="text-[11px] text-gray-500 mb-2">Saves this branch's Spaces (with their dashboards and side bars), the group mapping and the individual setups as a file, and loads one back in. Nothing is ever deleted by an import.</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={exportAll} data-testid="user-spaces-export"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current && fileRef.current.click()} data-testid="user-spaces-import"><Upload className="w-3.5 h-3.5 mr-1" /> Import</Button>
            <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={importFile} data-testid="user-spaces-import-file" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
